// Permuta (barter): part of a proposal's net value settled in services
// instead of cash. Pure helpers shared by the editor, the lead flow, the
// client-facing documents and the finance stats.
import { round2 } from "./budgetCalc";

export type BarterPeriod = "month" | "year";

export interface BarterSplit {
  barterPercent: number;
  /** Portion of the net value settled in services. */
  barterTotal: number;
  /** What is actually charged (net - barter). */
  cashTotal: number;
  /** 100% permuta on a non-zero proposal: nothing to bill. */
  isFull: boolean;
  isPartial: boolean;
}

export function clampBarterPercent(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  return Math.min(100, Math.max(0, percent));
}

export function barterSplit(netTotal: number, percent: number): BarterSplit {
  const barterPercent = clampBarterPercent(percent);
  const net = round2(netTotal || 0);
  const barterTotal = round2(net * (barterPercent / 100));
  const cashTotal = round2(net - barterTotal);
  return {
    barterPercent,
    barterTotal,
    cashTotal,
    isFull: net > 0 && cashTotal <= 0,
    isPartial: barterPercent > 0 && cashTotal > 0,
  };
}

// ── Mensalidade dentro da permuta (flows/permuta.md I7) ──────────────────────

export interface RecurringBillingLike {
  recurring_monthly_total?: number | null;
  barter_percent?: number | null;
  /** Operator's "Cobrar a mensalidade mesmo assim" switch — only meaningful with barter_percent > 0. */
  barter_charge_recurring?: boolean | null;
}

export interface RecurringBilling {
  /** Σ monthly amount of the recurring items (0 when there are none). */
  monthly: number;
  /** The monthly fee is part of the barter: not billed, no MP subscription. */
  inBarter: boolean;
  /** What actually gets billed every month (0 when inBarter). */
  billed: number;
}

/**
 * Any barter (1–100%) covers the whole monthly fee unless the operator flips
 * `barter_charge_recurring`. Partial barters do NOT prorate the fee — the
 * decision is binary (see flows/permuta.md, 2026-09-14).
 */
export function recurringBilling(b: RecurringBillingLike): RecurringBilling {
  const monthly = round2(Number(b.recurring_monthly_total) || 0);
  const inBarter =
    monthly > 0 &&
    clampBarterPercent(Number(b.barter_percent) || 0) > 0 &&
    !b.barter_charge_recurring;
  return { monthly, inBarter, billed: inBarter ? 0 : monthly };
}

/** Short pill label: null when there is no barter, "Permuta" for 100%, "Permuta 50%" otherwise. */
export function barterBadgeLabel(percent: number): string | null {
  const p = clampBarterPercent(percent);
  if (p <= 0) return null;
  if (p >= 100) return "Permuta";
  return `Permuta ${Number.isInteger(p) ? p : p.toFixed(1)}%`;
}

/** When the deal closed — stamped by the DB on the first signed/accepted, else creation. */
export function barterAgreedAt(b: { accepted_at?: string | null; created_at: string }): Date {
  return new Date(b.accepted_at ?? b.created_at);
}

export interface BarterBudgetLike {
  id: string;
  title: string;
  status: string;
  net_total: number;
  barter_percent: number;
  barter_total: number;
  barter_description?: string | null;
  recurring_monthly_total?: number | null;
  barter_charge_recurring?: boolean | null;
  accepted_at?: string | null;
  created_at: string;
  lead_id?: string | null;
  /** Joined lead: `lead` after normalizeBudget (useBudgets), raw `leads` from PostgREST. */
  lead?: { id: string; name: string } | null;
  leads?: { id: string; name: string } | null;
}

export interface BarterSettingsLike {
  barter_limit_amount: number;
  barter_limit_period: BarterPeriod;
}

export interface BarterRow {
  id: string;
  title: string;
  leadId: string | null;
  leadName: string | null;
  percent: number;
  barterTotal: number;
  cashTotal: number;
  description: string | null;
  /** Monthly fee covered by the barter (informational — never part of `agreed`/the cap). 0 when billed or absent. */
  recurringInBarter: number;
  agreedAt: Date;
}

export interface BarterSummary {
  period: BarterPeriod;
  /** "setembro/2026" or "2026". */
  periodLabel: string;
  start: Date;
  end: Date;
  /** Cap from settings; 0 = no cap. */
  cap: number;
  /** Σ barter_total of closed budgets in the period. */
  agreed: number;
  count: number;
  /** cap - agreed (never negative); null when there is no cap. */
  remaining: number | null;
  /** agreed / cap; null when there is no cap. */
  ratio: number | null;
  over: boolean;
  /** Σ (net - barter) of every closed budget in the period — the cash side of the comparison. */
  cashAgreed: number;
  rows: BarterRow[];
}

const CLOSED = new Set(["signed", "accepted"]);

export function barterPeriodRange(
  period: BarterPeriod,
  now: Date,
): { start: Date; end: Date; label: string } {
  const y = now.getFullYear();
  if (period === "year") {
    return { start: new Date(y, 0, 1), end: new Date(y + 1, 0, 1), label: String(y) };
  }
  const m = now.getMonth();
  const label = `${new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(now)}/${y}`;
  return { start: new Date(y, m, 1), end: new Date(y, m + 1, 1), label };
}

export function summarizeBarter(
  budgets: BarterBudgetLike[],
  settings: BarterSettingsLike | null | undefined,
  now: Date = new Date(),
): BarterSummary {
  const period: BarterPeriod = settings?.barter_limit_period === "month" ? "month" : "year";
  const cap = Math.max(0, Number(settings?.barter_limit_amount) || 0);
  const { start, end, label } = barterPeriodRange(period, now);

  const closed = budgets.filter((b) => {
    if (!CLOSED.has(b.status)) return false;
    const at = barterAgreedAt(b);
    return at >= start && at < end;
  });

  const rows: BarterRow[] = closed
    .filter((b) => (b.barter_percent ?? 0) > 0 && (b.barter_total ?? 0) > 0)
    .map((b) => {
      const split = barterSplit(b.net_total, b.barter_percent);
      const lead = b.lead ?? b.leads ?? null;
      return {
        id: b.id,
        title: b.title,
        leadId: b.lead_id ?? lead?.id ?? null,
        leadName: lead?.name ?? null,
        percent: split.barterPercent,
        barterTotal: round2(b.barter_total),
        cashTotal: round2(b.net_total - b.barter_total),
        description: b.barter_description?.trim() || null,
        recurringInBarter: recurringBilling(b).inBarter ? recurringBilling(b).monthly : 0,
        agreedAt: barterAgreedAt(b),
      };
    })
    .sort((a, b) => b.agreedAt.getTime() - a.agreedAt.getTime());

  const agreed = round2(rows.reduce((s, r) => s + r.barterTotal, 0));
  const cashAgreed = round2(
    closed.reduce((s, b) => s + ((b.net_total || 0) - (b.barter_total || 0)), 0),
  );

  return {
    period,
    periodLabel: label,
    start,
    end,
    cap,
    agreed,
    count: rows.length,
    remaining: cap > 0 ? Math.max(0, round2(cap - agreed)) : null,
    ratio: cap > 0 ? agreed / cap : null,
    over: cap > 0 && agreed > cap,
    cashAgreed,
    rows,
  };
}
